import { Post } from "../Post";
import { CustomDialog } from "../../ui/CustomDialog";
import { useNotify } from "../../hooks/NotifyProvider";
import { FaPenNib } from "react-icons/fa";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ReadingTimeline } from "../ReadingTimeline";
import { useContext } from "react";
import UserContext from "../UserProvider";
import { useReading, useReadingsByBook } from "../../hooks/useReading";
import {
  draftStatusLabel,
  useReadingDraft,
  useReadingDrafts,
} from "../../hooks/useReadingDraft";
import { usePostsByBook } from "../../hooks/usePost";
import { GiBookshelf } from "react-icons/gi";
import { Menu, MenuItem, Tooltip } from "@mui/material";
import { registerBook } from "../../api/book";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { enumToGenre } from "../../util";
import { motion } from "framer-motion";
import { useRequireLogin } from "../../hooks/useRequireLogin";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Chip,
  Box,
  Divider,
  Avatar,
  AvatarGroup,
  IconButton,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import { ReadingRegister } from "../ReadingRegister";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import { BookWithDesc } from "./BookWithDesc";
import { PrimaryButton } from "../../ui/PrimaryButton";
import { BookHighlightsSection } from "../highlight/BookHighlightsSection";

export const BookDetail = ({ book, updated, visible = true }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorAddEl, setAnchorAddEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [openRegister, setOpenRegister] = useState(false);
  const { user } = useContext(UserContext);
  const [bookForReading, setBookForReading] = useState();
  const { notify } = useNotify();
  const [openAdd, setOpenAdd] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isLoggedIn, LoginDialog, showLoginDialog } = useRequireLogin();
  const { registerReading, deleteReading, toDoing } = useReading();

  const {
    data: posts = [],
    isLoading: loadingPosts,
    isError: isPostsError,
  } = usePostsByBook(book?.id, book?.isbn);
  const {
    data: readingsForBook = [],
    isLoading: loadingReadings,
    isError: isReadingsError,
  } = useReadingsByBook(book?.id, book?.isbn);

  const loading = loadingPosts || loadingReadings;

  const myReading = useMemo(
    () =>
      user
        ? readingsForBook.find((r) => r.user.userId === user.userId)
        : undefined,
    [readingsForBook, user]
  );

  // Reviewダイアログの入力を下書きとして自動保存する
  const { data: drafts = [], isLoading: loadingDrafts } = useReadingDrafts(
    user?.userId
  );
  const { saveDraft, deleteDraft, status: draftStatus } = useReadingDraft(
    user?.userId
  );
  const draftBookId = myReading?.book?.bookId ?? bookForReading?.bookId;
  const draft = drafts.find((d) => d.book.bookId === draftBookId);
  const draftLabel = draftStatusLabel(draftStatus, !!draft);
  const handleDraftChange = useCallback(
    (values) => {
      if (draftBookId) {
        saveDraft({ bookId: draftBookId, ...values });
      }
    },
    [draftBookId, saveDraft]
  );
  const doing = useMemo(
    () => readingsForBook.filter((r) => r.statusType === "DOING"),
    [readingsForBook]
  );
  const done = useMemo(
    () => readingsForBook.filter((r) => r.statusType === "DONE"),
    [readingsForBook]
  );

  useEffect(() => {
    if (isPostsError || isReadingsError) {
      notify("Failed to loading. Please try again.", "error");
    }
  }, [isPostsError, isReadingsError, notify]);

  const handleOpenAdd = (event) => {
    if (!isLoggedIn()) return;
    setAnchorAddEl(event.currentTarget);
    setOpenAdd(true);
  };

  const handleCloseAdd = () => {
    setOpenAdd(false);
    setAnchorAddEl(null);
  };

  const handleStartReading = async () => {
    if (!isLoggedIn()) return;
    try {
      if (myReading) {
        await toDoing(myReading.readingId, {
          bookId: book.id,
          userId: user.userId,
        });
        updated && updated();
        notify("Start reading!", "success");
      }
    } catch (error) {
      notify("Failure add this book to your bookshelf.", "error");
    }
  };
  const handleDoneReading = async () => {
    if (!isLoggedIn()) return;
    try {
      if (myReading) {
        handleRegister();
        updated && updated();
      }
    } catch (error) {
      notify("Failure add this book to your bookshelf.", "error");
    }
  };

  const handleDelete = async () => {
    if (!isLoggedIn()) return;
    if (window.confirm("remove from bookshelf?")) {
      try {
        if (myReading) {
          await deleteReading(myReading.readingId, {
            bookId: book.id,
            userId: user.userId,
          });
          updated && updated();
        }
        notify("Success delete this from your bookshelf. ", "success");
        updated && updated();
      } catch (error) {
        notify("Failed to delete this book.", "error");
      }
    }
  };

  const handleRegisterWithNone = async () => {
    if (!isLoggedIn()) return;
    try {
      setIsSubmitting(true);
      const result = await registerBook(book);
      if (result) {
        const rParam = {
          bookId: result.data.bookId,
          userId: user.userId,
          rate: 0,
          thoughts: "",
          description: "",
          statusType: "NONE",
        };
        await registerReading(rParam, { sourceId: book.id });
        updated && updated();
        notify("Add want to read list.", "success");
      }
    } catch (error) {
      notify("Failed to add this book to your list.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegisterWithDoing = async () => {
    if (!isLoggedIn()) return;
    try {
      setIsSubmitting(true);
      const result = await registerBook(book);
      if (result) {
        const rParam = {
          bookId: result.data.bookId,
          userId: user.userId,
          rate: 0,
          thoughts: "",
          description: "",
          statusType: "DOING",
        };
        await registerReading(rParam, { sourceId: book.id });
        updated && updated();
        notify("Start reading!", "success");
      }
    } catch (error) {
      notify("Failed to add this book to your list.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegisterWithDone = async () => {
    if (!isLoggedIn()) return;
    try {
      setIsSubmitting(true);
      const result = await registerBook(book);
      setBookForReading(result.data);
      handleRegister();
    } catch (error) {
      notify("Failed. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = () => {
    setOpenRegister(true);
  };

  const handleCloseRegister = () => {
    setOpenRegister(false);
    if (anchorEl != null) {
      handleClose();
    } else if (anchorAddEl != null) {
      handleCloseAdd();
    }
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      {showLoginDialog && <LoginDialog />}
      <CustomDialog
        open={openRegister}
        title="Review"
        onClose={handleCloseRegister}
      >
        {loadingDrafts ? (
          <div className="flex justify-center items-center min-h-[150px]">
            <CircularProgress size={20} />
          </div>
        ) : (
          <ReadingRegister
            book={bookForReading}
            sourceId={book.id}
            updated={() => {
              if (draftBookId) {
                deleteDraft(draftBookId);
              }
              handleCloseRegister();
              updated && updated();
            }}
            reading={myReading && myReading} //ここreadingId引渡しに変えて、readingRegisterに検索させるようにする？
            initialValues={
              draft && {
                rate: draft.rate,
                thoughts: draft.thoughts,
                recommended: draft.recommended,
              }
            }
            onDraftChange={handleDraftChange}
          />
        )}
        {!loadingDrafts && (
          <div
            className={`mt-1 text-xs font-soft ${
              draftStatus === "error"
                ? "text-red-500"
                : "text-zinc-500 dark:text-zinc-400"
            }`}
          >
            {draftLabel}
          </div>
        )}
      </CustomDialog>
      <BookWithDesc book={book} maxLength={100} />
      <div className="flex place-items-center  mt-2 ml-1 mb-1">
        <div className="text-xs mr-1 text-stone-600 dark:text-stone-300">
          Genre:{" "}
        </div>
        <Chip
          label={enumToGenre(book.genre)}
          size="small"
          variant="outlined"
          sx={{
            "& .MuiChip-label": {
              fontSize: "0.65rem",
            },
            "&:hover .MuiChip-label": {
              textDecoration: "none",
            },
          }}
        />
        <div className="ml-2 text-xs text-stone-600 dark:text-stone-300 font-soft">
          Published : {book.publishedDate ? book.publishedDate : "-"}
        </div>
      </div>
      <Divider />
      <div className="mt-2 mb-2">
        {loading ? (
          <div className="flex justify-center items-center min-h-[150px]">
            <CircularProgress size={20} />
          </div>
        ) : (
          <>
            <div className="flex justify-between items-start w-full font-soft font-bold">
              <div className="ml-1">Your Reading Timeline</div>
              {myReading && visible && (
                <motion.div whileTap={{ scale: 0.9 }}>
                  <Tooltip title="Update Reading Status" arrow placement="top">
                    <IconButton
                      aria-label="more"
                      size="small"
                      onClick={handleOpen}
                    >
                      <ChangeCircleIcon sx={{ fontSize: 25 }} />
                    </IconButton>
                  </Tooltip>
                </motion.div>
              )}
            </div>
            <div className="mt-2  w-full flex justify-center">
              {myReading ? (
                <ReadingTimeline reading={myReading} />
              ) : (
                visible && (
                  <div>
                    <motion.div whileTap={{ scale: 0.95 }}>
                      <div className="mt-1 flex justify-center">
                        <PrimaryButton
                          endIcon={!isSubmitting && <GiBookshelf />}
                          disabled={isSubmitting}
                          onClick={handleOpenAdd}
                          sx={{ width: "160px" }}
                        >
                          {isSubmitting ? (
                            <CircularProgress
                              size={20}
                              sx={{ color: "background.default" }}
                            />
                          ) : (
                            "Add bookshelf"
                          )}
                        </PrimaryButton>
                      </div>
                    </motion.div>
                    <div className="text-sm text-zinc-700 dark:text-zinc-300 font-soft">
                      Not on your Bookshelf yet.
                    </div>
                  </div>
                )
              )}
            </div>
            <Menu
              className="absolute bottom-0"
              // id="long-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
            >
              <div>
                {!myReading ? (
                  <>
                    <MenuItem
                      onClick={() => {
                        handleRegisterWithNone();
                        handleClose();
                      }}
                    >
                      <ListItemIcon>
                        <BookmarkBorderIcon fontSize="small" />
                      </ListItemIcon>
                      <div className="font-soft">To Read</div>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleRegisterWithDoing();
                        handleClose();
                      }}
                    >
                      <ListItemIcon>
                        <MenuBookIcon fontSize="small" />
                      </ListItemIcon>
                      <div className="font-soft">Reading</div>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleRegisterWithDone();
                      }}
                    >
                      <ListItemIcon>
                        <CheckCircleIcon fontSize="small" />
                      </ListItemIcon>
                      <div className="font-soft">Completed!</div>
                    </MenuItem>
                  </>
                ) : myReading.statusType === "DONE" ? (
                  <>
                    {!myReading.rate && !myReading.thoughts && (
                      <>
                        <MenuItem
                          onClick={() => {
                            handleDoneReading();
                            handleClose();
                          }}
                        >
                          <ListItemIcon>
                            <div className="ml-1">
                              <FaPenNib fontSize="small" color="error" />
                            </div>
                          </ListItemIcon>
                          <div className="font-soft">review</div>
                        </MenuItem>
                        <Divider />
                      </>
                    )}
                    <MenuItem
                      onClick={() => {
                        handleDelete();
                        handleClose();
                      }}
                    >
                      <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                      </ListItemIcon>
                      <div className="font-soft">Delete from bookshelf</div>
                    </MenuItem>
                  </>
                ) : myReading.statusType === "DOING" ? (
                  <>
                    <MenuItem
                      onClick={() => {
                        handleDoneReading();
                      }}
                    >
                      <ListItemIcon>
                        <CheckCircleIcon fontSize="small" />
                      </ListItemIcon>
                      <div className="font-soft">Completed!</div>
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        handleDelete();
                        handleClose();
                      }}
                    >
                      <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                      </ListItemIcon>
                      <div className="font-soft">Delete from bookshelf</div>
                    </MenuItem>
                  </>
                ) : (
                  <>
                    <MenuItem
                      onClick={() => {
                        handleStartReading();
                        handleClose();
                      }}
                    >
                      <ListItemIcon>
                        <MenuBookIcon fontSize="small" />
                      </ListItemIcon>
                      <div className="font-soft">Reading now</div>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleDoneReading();
                      }}
                    >
                      <ListItemIcon>
                        <CheckCircleIcon fontSize="small" />
                      </ListItemIcon>
                      <div className="font-soft">Completed!</div>
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        handleDelete();
                        handleClose();
                      }}
                    >
                      <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                      </ListItemIcon>
                      <div className="font-soft">Delete from bookshelf</div>
                    </MenuItem>
                  </>
                )}
              </div>
            </Menu>
            <Menu
              className="absolute bottom-0"
              anchorEl={anchorAddEl}
              open={openAdd}
              onClose={handleCloseAdd}
            >
              <div>
                {!myReading && (
                  <>
                    <MenuItem
                      onClick={() => {
                        handleRegisterWithNone();
                        handleCloseAdd();
                      }}
                    >
                      <ListItemIcon>
                        <BookmarkBorderIcon fontSize="small" />
                      </ListItemIcon>
                      <div className="font-soft">To Read</div>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleRegisterWithDoing();
                        handleCloseAdd();
                      }}
                    >
                      <ListItemIcon>
                        <MenuBookIcon fontSize="small" />
                      </ListItemIcon>
                      <div className="font-soft">Reading</div>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleRegisterWithDone();
                      }}
                    >
                      <ListItemIcon>
                        <CheckCircleIcon fontSize="small" />
                      </ListItemIcon>
                      <div className="font-soft">Completed!</div>
                    </MenuItem>
                  </>
                )}
              </div>
            </Menu>
          </>
        )}
      </div>
      {myReading && (
        <>
          <Divider />
          <BookHighlightsSection book={book} reading={myReading} userId={user?.userId} />
        </>
      )}
      <Divider />
      <div className="flex mt-4 mb-4 justify-evenly">
        <div className="flex place-items-center">
          {doing.length !== 0 ? (
            <AvatarGroup
              max={3}
              spacing="small"
              sx={{
                "& .MuiAvatar-root": {
                  width: 34,
                  height: 34,
                  fontSize: 16,
                },
              }}
            >
              {doing.map((reading) => (
                <Avatar
                  key={reading.readingId}
                  alt={reading.user.name}
                  src={reading.user.picture}
                />
              ))}
            </AvatarGroup>
          ) : (
            <div className="text-sm font-soft">No one</div>
          )}

          <div className="ml-1 text-sm font-soft">reading now.</div>
        </div>
        <div className="flex place-items-center">
          <div>
            {done.length !== 0 ? (
              <AvatarGroup
                max={3}
                spacing="small"
                sx={{
                  "& .MuiAvatar-root": {
                    width: 34,
                    height: 34,
                    fontSize: 16,
                  },
                }}
              >
                {done.map((reading) => (
                  <Avatar alt={reading.user.name} src={reading.user.picture} />
                ))}
              </AvatarGroup>
            ) : (
              <div className="text-sm font-soft">No one</div>
            )}
          </div>
          <div className="ml-1 text-sm font-soft">read it.</div>
        </div>
      </div>
      <Divider />
      <div className="text-lg mb-2 mt-3 font-soft font-bold">
        Readers' comments
      </div>
      {loading ? (
        <div className="flex justify-center items-center min-h-[150px]">
          <CircularProgress size={20} />
        </div>
      ) : posts.length >= 1 ? (
        <>
          {posts.map((post) => (
            <>
              <Box key={post.postId} sx={{ width: "100%" }}>
                <Post post={post} fromDetail={true} />
              </Box>
              <>
                {posts.length >= 2 && <Divider sx={{ width: "100%" }} />}
              </>
            </>
          ))}
        </>
      ) : (
        <div className="mt-2 flex justify-center text-stone-600 dark:text-stone-300 text-sm font-soft">
          no comments yet.
        </div>
      )}
    </div>
  );
};
